import { useRef } from 'react';
import styled from '@emotion/styled';
import { toPlainText } from '@portabletext/react';
import { useLiveQuery } from 'next-sanity/preview';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { BlogSchema } from '@/components/common/blogSchema';
import { screen } from '@/components/common/breakpoints';
import { BlogCaseLayout } from '@/components/layout/BlogCaseLayout';
import { Section } from '@/components/layout/Section';
import { AuthorTagBlock } from '@/components/page/blog/AuthorTagBlock';
import { dateTimeFormat, formatDate } from '@/components/page/blog/blogUtils';
import { PostCopy } from '@/components/page/blog/PostCopy';
import { PostPageHero } from '@/components/page/blog/PostPageHero';
import { PostSidebar } from '@/components/page/blog/PostSidebar';
import { RelatedPosts } from '@/components/page/blog/RelatedPosts';
import { BASE_REVALIDATE } from '@/lib/constants';
import {
  allBlogPostsQuery,
  blogPostQuery,
  blogSettingsQuery,
  localeTranslationsQuery,
  navigationCollectionQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { blocksToText, getClient, readToken } from '@/lib/sanityUtils';
import { PageCommonProvider } from '@/lib/usePageCommon';
import { getSettledValue, getValidLocale } from '@/lib/utils';

import type { SanityBlogPostTocDataType } from '@/components/page/blog/PostTableOfContents';
import type {
  BlogAuthor,
  BlogCategory,
  BlogPost as BlogPostType,
  BlogSettings,
} from '@/sanity/sanity.types';
import type {
  BlogPostPreview,
  Navigation_exType,
  SiteSettings,
  Translation,
} from '@/types/shared';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';

export interface BlogPostTypeExtended
  extends Omit<BlogPostType, 'author' | 'category'> {
  tocData: SanityBlogPostTocDataType;
  relatedPosts: BlogPostPreview[][];
  author: BlogAuthor;
  category: BlogCategory[];
}

export const getStaticPaths = (async () => {
  const allBlogPosts = await getClient().fetch(allBlogPostsQuery());

  const paths = allBlogPosts.map((post) => ({
    params: { slug: post.slug.current },
    locale: post.language,
  }));

  return { paths, fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({
  params: { slug },
  locale,
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const validLocale = getValidLocale(locale);

  const [blogPost, blogSettings, siteSettings, navigationCollection] =
    await Promise.allSettled([
      client.fetch<BlogPostTypeExtended>(
        blogPostQuery({ slug: slug as string, locale: validLocale }),
      ),
      client.fetch<BlogSettings>(blogSettingsQuery({ locale: validLocale })),
      client.fetch<SiteSettings>(siteSettingsQuery({ locale: validLocale })),

      client.fetch<Navigation_exType[]>(
        navigationCollectionQuery({ locale: validLocale }),
      ),
    ]);

  const blogPostData = getSettledValue(blogPost);

  const translations = await client.fetch<Translation[]>(
    localeTranslationsQuery(blogPostData),
  );

  if (blogPostData) {
    return {
      props: {
        blogPost: blogPostData,
        blogSettings: getSettledValue(blogSettings),
        siteSettings: getSettledValue(siteSettings),

        navigationCollection: getSettledValue(navigationCollection),
        slug,
        locale: validLocale,
        draftMode,
        token: draftMode ? readToken : '',
        translations,
      },
      revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
    };
  }

  return {
    notFound: true,
  };
}) satisfies GetStaticProps;

const BlogPost = ({
  blogPost,
  blogSettings,
  siteSettings,

  navigationCollection,
  draftMode,
  translations,
  locale,
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { asPath } = useRouter();

  const [data] = useLiveQuery<BlogPostTypeExtended>(
    blogPost,
    blogPostQuery({ locale, slug: slug as string }),
  );

  const postContentRef = useRef<HTMLDivElement>(null);

  // Excerpts cannot contain images. We also only one the first paragraph (but in an array)
  const excerpt = data?.postBody
    .filter((item) => item._type !== 'blogPostImage')
    .slice(0, 1);

  const featuredImage =
    data?.featuredImage ?? blogSettings?.featuredImageDefault;

  // Multiple related posts can be on the page, and they can sometimes contain nothing, which needs to be filtered for.
  const postData =
    data.relatedPosts
      ?.flat()
      ?.filter((posts) => Boolean(Object.keys(posts).length))
      ?.filter(
        (post, index, self) =>
          index === self.findIndex((t) => t._id === post._id),
      ) ?? [];

  const headline = blocksToText(data?.heading);
  const articleBody = blocksToText(data?.postBody);

  return (
    <BlogCaseLayout
      SEO={data?.SEO}
      localeTranslations={translations}
      siteSettings={siteSettings}
      preview={draftMode}
      navigationCollection={navigationCollection}
      dynamicYield={{
        contextPageType: 'CATEGORY',
        contextDataType: 'Blog',
      }}
    >
      <Head>
        <BlogSchema
          url={asPath}
          datePublished={data?.publishDate}
          headline={headline}
          author={data?.author}
          description={toPlainText(excerpt)}
          articleBody={articleBody}
          image={featuredImage}
          locale={locale}
        />
      </Head>
      <PageCommonProvider siteSettings={siteSettings}>
        <PostPageHero
          heading={data?.heading}
          category={data?.category[0]}
          author={data?.author}
          publishDate={formatDate(data?.publishDate)}
          datetimeDate={dateTimeFormat(data?.publishDate)}
          isDecorativeImage
          backgroundColour={data?.backgroundColour}
          featuredImage={featuredImage}
          fallback={blogSettings?.featuredImageDefault}
        />

        {Boolean(data?.postBody) && (
          <Section verticalPadding className="blog-post-body">
            <PostContent ref={postContentRef}>
              <PostSidebar tocData={data.tocData} />
              <PostCopy excerpt={excerpt} blogPost={data} />
            </PostContent>
          </Section>
        )}

        {!!data?.author && (
          <AuthorTagBlock author={data?.author} tags={data?.tags} />
        )}
        {postData.length > 0 && (
          <RelatedPosts
            backgroundColour={data?.backgroundColour}
            posts={postData}
            fallbackAuthorImage={blogSettings?.authorImageDefault}
            fallbackImage={blogSettings?.featuredImageDefault}
          />
        )}
      </PageCommonProvider>
    </BlogCaseLayout>
  );
};

const PostContent = styled.div`
  display: flex;
  gap: var(--spacing-x-large);
  flex-direction: column;

  ${screen.md} {
    flex-direction: row;
  }
`;

export default BlogPost;
